import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import AutoGraphRounded from '@mui/icons-material/AutoGraphRounded'
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded'
import LocalOfferRounded from '@mui/icons-material/LocalOfferRounded'
import { Box, Skeleton, Typography } from '@mui/material'

import { INTENSITY_MEDIUM_MIN } from '../../helpers/intensityThresholds'
import { displayedScoreGap, formatDelta } from '../../helpers/scoreDisplay'
import type { PatternMixRow } from '../../helpers/showcase'
import {
  buildCompareOverviewTldrText,
  buildGameScopeTitle,
  clampPct,
  deltaColor,
  differentiationDelta,
  getPatternMixRows,
  getTopDifferentiatingPatterns,
} from '../../helpers/showcase'
import { useCompareStoryPatterns } from '../../hooks/useCompareStoryPatterns'
import { useMonthLabel } from '../../hooks/useMonthLabel'
import { CompareTab, useUAInsightsScope } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { GameCreativeDetailsData, StoryPattern, TermRef } from '../../types'
import type { SharedTag } from '../CompareSharedTagsSection/CompareSharedTagsSection'
import { CompareSharedTagsSection } from '../CompareSharedTagsSection/CompareSharedTagsSection'
import { ComparisonDiagnosis } from '../ComparisonDiagnosis/ComparisonDiagnosis'
import { ComparisonDiagnosisLegend } from '../ComparisonDiagnosisLegend/ComparisonDiagnosisLegend'
import { KpiCard } from '../KpiCard/KpiCard'
import { MonthlyTldrCard } from '../MonthlyTldrCard/MonthlyTldrCard'
import { EmptyHint, ViewAllButton } from '../OverviewPrimitives/OverviewPrimitives'
import { PatternMixChart } from '../PatternMixChart/PatternMixChart'
import { SectionCard } from '../SectionCard/SectionCard'

/** Story Pattern Mix rows previewed (the largest source-vs-target divergences). */
const MIX_PREVIEW = 5

interface CompareOverviewTabProps {
  /** Source game (left side of the matchup). */
  source: GameCreativeDetailsData
  /**
   * Comparison target. For a game-vs-genre matchup this is the genre wrapped
   * in a `GameCreativeDetailsData` shape (only `game.name`, `motivationScores.
   * creativeAds`, `tags` and `creativeAds` carry meaningful values), so the
   * overview never needs a genre-specific branch.
   */
  target: GameCreativeDetailsData
  /** Source game id, used to load the cross-target Story Patterns mix. */
  sourceGameId: string
  /** Target game id (game-vs-game), mutually exclusive with `targetGenreId`. */
  targetGameId?: string | null
  /** Target genre id (game-vs-genre), mutually exclusive with `targetGameId`. */
  targetGenreId?: string | null
  /** Switch the active compare-result tab (used by the "View all" affordances). */
  onSelectTab: (tab: CompareTab) => void
  /**
   * Open the Creative Tags tab scrolled to a specific tag — used when the user
   * clicks one of the shared-tag preview rows. Falls back to a plain tab switch
   * when not supplied.
   */
  onOpenCreativeTag?: (tagId: string) => void
}

/** A single motivation classified against the medium-intensity threshold on both sides. */
interface DiagnosedMotivation {
  motivation: TermRef
  source: number
  target: number
}

/**
 * "Overview" tab for the UA Insights compare dialogs (game-vs-game and
 * game-vs-genre) — the default landing tab. It stitches together a one-glance
 * summary of every other compare tab (Motivations, Story Patterns, Creative
 * Tags): the headline matchup numbers, the shared / diverging motivations, the
 * patterns where the two sides' momentum diverges most, and the tags they
 * share. Each KPI / "View all" affordance routes the user into the matching tab
 * for the full picture.
 *
 * Motivations are classified on the Creative Ads signal (the same series the
 * Motivations tab's radar + diagnosis use) so the Overview's shared / diverging
 * tallies stay calibrated with the deeper view. Story Patterns reuse
 * `useCompareStoryPatterns` exactly as the Story Patterns tab does, so the
 * top-differentiating preview is the identical set.
 */
export const CompareOverviewTab: FC<CompareOverviewTabProps> = ({
  source,
  target,
  sourceGameId,
  targetGameId,
  targetGenreId,
  onSelectTab,
  onOpenCreativeTag,
}) => {
  const { t } = useTranslation('ua-insights')
  const { patternLabel, motivationLabel, tagLabel } = useUATaxonomy()
  const { month } = useUAInsightsScope()
  const { data: storyData, isLoading: storyLoading } = useCompareStoryPatterns(sourceGameId, targetGameId ?? null, targetGenreId ?? null)

  const sourceName = source.game.name
  const targetName = target.game.name

  // --- motivation alignment (Creative Ads signal, positional zip) -----------
  // Both arrays follow the canonical MOTIVATIONS order, so a positional zip is
  // enough — no name lookup needed. Mirrors `ComparisonDiagnosis.classifyPair`.
  const { shared, diverging } = useMemo(() => {
    const sourceScores = source.motivationScores.creativeAds
    const targetScores = target.motivationScores.creativeAds
    const sharedRows: DiagnosedMotivation[] = []
    const divergingRows: DiagnosedMotivation[] = []
    for (let i = 0; i < sourceScores.length; i += 1) {
      const s = sourceScores[i]
      const tgt = targetScores[i]
      if (!tgt) continue
      const sHigh = s.avgIntensity >= INTENSITY_MEDIUM_MIN
      const tHigh = tgt.avgIntensity >= INTENSITY_MEDIUM_MIN
      const row: DiagnosedMotivation = { motivation: s.motivation, source: s.avgIntensity, target: tgt.avgIntensity }
      if (sHigh && tHigh) sharedRows.push(row)
      else if (sHigh !== tHigh) divergingRows.push(row)
    }
    // Sort the divergences by the displayed (1-decimal) gap so the Overview's
    // "widest" divergence — surfaced in the KPI strip and the Monthly Insight —
    // matches the order and "biggest gap" call-out of the Motivation Alignment
    // diagnosis, which sorts on the same rounded gap.
    sharedRows.sort((a, b) => b.source + b.target - (a.source + a.target))
    divergingRows.sort((a, b) => Math.abs(displayedScoreGap(b.source, b.target)) - Math.abs(displayedScoreGap(a.source, a.target)))
    return { shared: sharedRows, diverging: divergingRows }
  }, [source.motivationScores.creativeAds, target.motivationScores.creativeAds])

  // --- story patterns (top differentiating, mirrors the Story Patterns tab) --
  const gamePatterns = useMemo<StoryPattern[]>(() => (storyData ? storyData.patterns.filter((p) => p.creativesCount > 0) : []), [storyData])
  const topDifferentiating = useMemo(() => getTopDifferentiatingPatterns(gamePatterns), [gamePatterns])
  const differentiatingCards = useMemo(
    () =>
      topDifferentiating.map((p) => {
        const diff = differentiationDelta(p)
        return { pattern: p.pattern, deltaPp: diff, delta: formatDelta(diff) }
      }),
    [topDifferentiating]
  )

  // Story Pattern Mix preview — mirrors the Story Patterns tab's headline card
  // (the source game's pattern share vs. the comparison target). Built from the
  // full pattern list (so target-only patterns the source skips can still
  // surface as under-indexes), trimmed to the largest absolute divergences and
  // re-sorted signed so the diverging chart reads cleanly.
  const patternMixPreview = useMemo<PatternMixRow[]>(
    () =>
      storyData
        ? [...getPatternMixRows(storyData.patterns, patternLabel)]
            .sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap))
            .slice(0, MIX_PREVIEW)
            .sort((a, b) => b.gap - a.gap)
        : [],
    [storyData, patternLabel]
  )

  // --- creative tags overlap ------------------------------------------------
  // Compare on share-of-creatives (penetration) rather than raw occurrence
  // counts: the two sides usually have very different creative volumes (a game
  // vs a whole genre), so raw counts mostly track that volume and pin every bar
  // near full width. Share (% of each side's creatives that use the tag) is the
  // same normalised signal the deep Creative Tags tab surfaces as "Y% of game",
  // so the Overview preview stays calibrated with it.
  const sourceTotalCreatives = source.creativeAds.length
  const targetTotalCreatives = target.creativeAds.length
  const sharedTags = useMemo<SharedTag[]>(() => {
    const targetById = new Map(target.tags.map((tg) => [tg.tag.id, tg.count]))
    const toShare = (count: number, total: number) => (total > 0 ? clampPct((count / total) * 100) : 0)
    return source.tags
      .filter((tg) => targetById.has(tg.tag.id))
      .map((tg) => {
        const sourceCount = tg.count
        const targetCount = targetById.get(tg.tag.id) ?? 0
        return {
          tag: tg.tag,
          sourceCount,
          targetCount,
          sourceShare: toShare(sourceCount, sourceTotalCreatives),
          targetShare: toShare(targetCount, targetTotalCreatives),
        }
      })
      .sort((a, b) => b.sourceShare + b.targetShare - (a.sourceShare + a.targetShare))
  }, [source.tags, target.tags, sourceTotalCreatives, targetTotalCreatives])

  // --- Monthly Insight (deterministic, templated narrative) -----------------
  const formatMonth = useMonthLabel()
  const monthLabel = formatMonth(month)
  const tldrTitle = buildGameScopeTitle(`${sourceName} ${t('compare_vs')} ${targetName}`, monthLabel)
  const tldrText = buildCompareOverviewTldrText(t, {
    sourceName,
    targetName,
    sharedCount: shared.length,
    topDiverging: diverging[0] ? motivationLabel(diverging[0].motivation) : undefined,
    topDifferentiatingPattern: differentiatingCards[0]
      ? { theme: patternLabel(differentiatingCards[0].pattern), delta: differentiatingCards[0].delta }
      : undefined,
    sharedTagCount: sharedTags.length,
  })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* KPI strip — the at-a-glance matchup headline numbers, each routing
          onward. There's no "shared motivations" tile: the shared-motivations
          drilldown was dropped, so a headline count with no destination would
          be a dead-end affordance. The shared tally still lives in the Monthly
          Insight narrative below, and the comparison's actionable signal is
          divergence, surfaced by the remaining motivations tile + diagnosis. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        <KpiCard
          icon={<CompareArrowsRounded />}
          label={t('compare_overview_kpi_diverging_motivations')}
          primary={diverging.length.toString()}
          secondary={
            diverging[0] ? (
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary' }}>
                {motivationLabel(diverging[0].motivation)}
              </Typography>
            ) : undefined
          }
          color="error"
          onClick={() => onSelectTab('motivations')}
        />
        <KpiCard
          icon={<AutoGraphRounded />}
          label={t('compare_overview_kpi_top_pattern')}
          primary={differentiatingCards[0] ? patternLabel(differentiatingCards[0].pattern) : '—'}
          secondary={
            differentiatingCards[0] ? (
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: deltaColor(differentiatingCards[0].deltaPp) }}>
                {differentiatingCards[0].delta}
              </Typography>
            ) : undefined
          }
          loading={storyLoading}
          color="info"
          onClick={() => onSelectTab('storyPatterns')}
        />
        <KpiCard
          icon={<LocalOfferRounded />}
          label={t('compare_overview_kpi_shared_tags')}
          primary={sharedTags.length.toString()}
          secondary={
            sharedTags[0] ? (
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary' }}>
                {tagLabel(sharedTags[0].tag)}
              </Typography>
            ) : undefined
          }
          color="primary"
          onClick={() => onSelectTab('creativeTags')}
        />
      </Box>

      {/* Monthly Insight — narrative summary generated from the stats above. */}
      <MonthlyTldrCard title={tldrTitle} text={tldrText} />

      {/* Motivation Alignment — the Overview surfaces the Motivations tab's
          cross-entity diagnosis verbatim (summary card, gap legend and the
          per-motivation diverging cards), so the snippet reads identically and
          routes onward into the full Motivations tab. */}
      <SectionCard
        title={t('compare_motivation_diagnosis_title')}
        headerCenter={
          diverging.length > 0 ? (
            <ComparisonDiagnosisLegend source={{ name: sourceName, icon: source.game.icon }} target={{ name: targetName, icon: target.game.icon }} />
          ) : undefined
        }
        headerAction={
          <ViewAllButton onClick={() => onSelectTab('motivations')} label={t('overview_open_section', { section: t('compare_tab_motivations') })} />
        }
      >
        <ComparisonDiagnosis
          source={{ name: sourceName, icon: source.game.icon, scores: source.motivationScores.creativeAds }}
          target={{ name: targetName, icon: target.game.icon, scores: target.motivationScores.creativeAds }}
          unavailableMessage={t('compare_diagnosis_unavailable')}
          hideSummary
          hideLegend
        />
      </SectionCard>

      {/* Story Pattern Mix — how the source game's pattern share diverges from
          the comparison target. Previews the largest over/under-indexes as
          compact diverging bars, mirroring the Story Patterns tab's headline. */}
      <SectionCard
        title={t('overview_pattern_mix_heading', { benchmark: targetName })}
        headerAction={
          <ViewAllButton onClick={() => onSelectTab('storyPatterns')} label={t('overview_open_section', { section: t('compare_tab_story_patterns') })} />
        }
      >
        {storyLoading ? (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={96} sx={{ flex: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : patternMixPreview.length > 0 ? (
          <Box>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.5, mb: 1.25 }}>
              {t('pattern_mix_hint', { benchmark: targetName })}
            </Typography>
            <PatternMixChart
              rows={patternMixPreview}
              gameName={sourceName}
              benchmarkLabel={targetName}
              onSelectPattern={() => onSelectTab('storyPatterns')}
              variant="compact"
            />
          </Box>
        ) : (
          <EmptyHint text={t('compare_story_patterns_empty')} />
        )}
      </SectionCard>

      {/* Creative Tags — the tactics both sides lean on, with per-side counts. */}
      <CompareSharedTagsSection
        sharedTags={sharedTags}
        sourceName={sourceName}
        targetName={targetName}
        sourceIcon={source.game.icon}
        targetIcon={target.game.icon}
        sourceTotalCreatives={sourceTotalCreatives}
        targetTotalCreatives={targetTotalCreatives}
        onViewAll={() => onSelectTab('creativeTags')}
        onOpenTag={(tagId) => (onOpenCreativeTag ? onOpenCreativeTag(tagId) : onSelectTab('creativeTags'))}
      />
    </Box>
  )
}
